import { useEffect, useState, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CategoriesList from "cxfcomponents/Lists/CategoriesList";
import SoftAvatar from "components/SoftAvatar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import { ThemedPeopleIcon } from "assets/CXFIcons/Portal/Dashboard";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Badge from "@mui/material/Badge";


import ActionCellfromUser from "layouts/pages/users/reports/components/Reviews/ActionCellfromUser";

import { useAuth0 } from "@auth0/auth0-react";

import { UsersStatusContext } from "context/AppProvider/UsersStatusContext";

import { fetchUsers } from "redux/actions/flowDesigner/usersActionCreator";


import "react-toastify/dist/ReactToastify.css";

import DefaultUserAvatar from "layouts/pages/account/components/Header/components/DefaultUserAvatar";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { useRoles } from "hooks/useRoles";
import { PERMISSIONS } from "constants/portal";
import TransferOwnershipDialog from "layouts/pages/users/reports/components/Reviews/TransferOwnershipDialog";

const BottomAction = ({ hasPermissionToViewUsers }) => {
	if (!hasPermissionToViewUsers) return <></>;

	return (
		<SoftBox>
			<SoftButton
				sx={{ float: "right" }}
				component={Link}
				to={hasPermissionToViewUsers ? "/administration/users" : "#"}
				variant="text"
				color="primary"
			>
				View All <ArrowForwardIcon sx={{ marginLeft: "12px" }} />
			</SoftButton>
		</SoftBox>
	);
};

const MyTeam = () => {
	const { user } = useAuth0();
	const { usersStatus } = useContext(UsersStatusContext);
	const [categories, setCategories] = useState([]);
	const [isOwner, setIsOwner] = useState(false);
	const [transferTarget, setTransferTarget] = useState(null);
	const { users, isLoadingUsers } = useSelector(
		(store) => store?.portal?.users
	);
	const { roles } = useRoles(false);
	const { t } = useTranslation("dashboard");
	const dispatch = useDispatch();
	const { userPermissions } = useContext(PermissionsContext);

	const getUsers = () => {
		dispatch(fetchUsers(user?.stripeId));
	};

	const checkUsersStatus = (auth0_id) => {
		return usersStatus?.find(
			(userStatus) => userStatus?.auth0_id === auth0_id
		)?.status;
	};

	const handleCloseTransferDialog = () => setTransferTarget(null);


	const renderUserWithStatusIcon = (
		auth0_id,
		first_name,
		last_name,
		image
	) => {
		return (
			<SoftBox width="3.75">
				<Badge
					variant="dot"
					color={
						checkUsersStatus(auth0_id) === "active" ? "success" : "dark"
					}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "right"
					}}
					size="large"
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						position: "relative",
						"& .MuiBadge-dot": {
							borderRadius: "50%",
							zIndex: "initial",
							bottom: 4,
							right: 4
						}
					}}
				>
					{image && image.trim() ? (
						<SoftAvatar
							alt={`${first_name} ${last_name}`}
							size="sm"
							src={image}
						/>
					) : (
						<DefaultUserAvatar
							firstName={first_name}
							lastName={last_name}
							size="sm"
							border="unset"
							variant="rounded"
							disableFocusListener={false}
							disableTouchListener={false}
							open={false}
						/>
					)}
				</Badge>
			</SoftBox>
		);
	};

	const openTransferDialog = (username, auth0Id) => setTransferTarget({ username, auth0Id });


	useEffect(() => {
		const categories = users.slice(0, 3)?.map((user) => ({
			color: "text",
			icon: renderUserWithStatusIcon(
				user?.auth0_id,
				user?.first_name,
				user?.last_name,
				user?.image
			),
			name: (
				<Link
					to={`/account/users/profile-overview/${user?.auth0_id}`}
					style={{ textDecoration: "none", color: "inherit" }}
				>
					{`${user?.first_name} ${user?.last_name}`}
				</Link>
			),
			//description: user?.email_address,
			description: (
				<Link
					to={`/account/users/profile-overview/${user?.auth0_id}`}
					style={{ textDecoration: "none", color: "inherit" }}
				>
					{user?.email_address}
				</Link>
			),
			// route: `/administration/users/${user?.auth0_id}`,
			status: user?.status,
			userAction: (
				<ActionCellfromUser
					auth0Id={user?.auth0_id}
					isOwner={isOwner}
					username={`${user?.first_name} ${user?.last_name}`}
					openTransferDialog={openTransferDialog}
					roleName={roles?.find((role) => role.id === user?.role_id)?.name}
					roles={roles}
					users={users}
					tenantId={user?.tenant_id}
					getUsers={getUsers}
					status={user?.status}
					currentUser={user}
					actionOrientation="vertical"
				/>
			)
		}));

		setCategories(categories);
	}, [users, usersStatus, roles]);

	useEffect(() => {
		if (roles && users) {
			const ownerRoleId = roles?.find(
				(role) => role?.name?.toLowerCase() === "owner"
			)?.id;
			const userRoleId = users?.find(
				(item) => item?.auth0_id === user?.cxfUserId
			)?.role_id;
			ownerRoleId === userRoleId && setIsOwner(true);
		}
	}, [users, roles]);

	return (
		<>
			<CategoriesList
				title={t("labels.my_team")}
				categories={categories}
				headerIcon={
					<ThemedPeopleIcon sx={{ position: "relative", top: "3px" }} />
				}
				isLoading={isLoadingUsers}
				noDataMessage={t("messages.no_members_found")}
				bottomAction={
					<BottomAction
						hasPermissionToViewUsers={
							userPermissions.find(
								(permission) =>
									permission.name === PERMISSIONS.ACCESS_TO_USERS_PAGE
							)?.value ?? false
						}
					/>
				}
				isUser
			/>
			<TransferOwnershipDialog
				open={Boolean(transferTarget)}
				onClose={handleCloseTransferDialog}
				onUsersChanged={getUsers}
				targetUser={transferTarget}
			/>
		</>
	);
};

export default MyTeam;
